import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProfitPanel } from "@/components/ProfitPanel";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const today = () => new Date().toISOString().slice(0, 10);
const yearStart = () => {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-04-01`;
};

export default function ProfitReport() {
  const [profile, setProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profitability", {
        params: { customer, date_from: from, date_to: to },
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/customer").then(({ data }) => setCustomers(data.map((c) => c.name))).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (format) => {
    try {
      const res = await api.get("/profitability/export", {
        params: { customer, date_from: from, date_to: to, format },
        responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `profit-report.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Profit Report</h1>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div>
            <L>From</L>
            <DateInput data-testid="profit-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="profit-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div>
            <L>Customer</L>
            <Select value={customer || "__all__"} onValueChange={(v) => setCustomer(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="profit-customer-select" className="mt-2 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All customers</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="profit-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={load}>
            Run report
          </Button>
          <div className="flex gap-2">
            <Button data-testid="profit-pdf-button" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={15} className="mr-2" /> PDF
            </Button>
            <Button data-testid="profit-excel-button" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              <FileSpreadsheet size={15} className="mr-2" /> Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : !data || data.totals.bills === 0 ? (
          <div data-testid="profit-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">No sales in this period</h3>
            <p className="mt-1 text-sm text-zinc-500">Pick another date range or customer.</p>
          </div>
        ) : (
          <ProfitPanel profit={data} title={customer ? `Profit — ${customer}` : "Profit — all customers"} />
        )}
      </main>
    </AppShell>
  );
}
